import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { peekAsyncJobIdFromResponse } from 'app/services/http-async-job-response.util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IProgramItemsWithGroupQuery } from '../interfaces/program-items-with-group.query.interface';
import { ICreateProgramItemActionPayload } from '../interfaces/create-program-item.action.payload.interface';
import { ProgramItemCommand } from '../models/program-item.command.model';
import { ProgramItem } from '../models/program-item.model';
import { IProgramItemQuery } from '../interfaces/program-item.query.interface';
import { ProgramItemsWithGroup } from '../models/program-items-with-group.model';
import { UpdateProgramItemCommand } from '../models/update-program-item.command.model';
import { IAddCourseOfferingsToCourseCategoryActionPayload } from '../interfaces/add-course-offerings-to-course-category.action.payload.interface';
import { AddCourseOfferingsToCourseGroupCommand } from '../models/add-course-offerings-to-course-group.command.model';
import { IBookAllParticipantsToCourseExecutionActionPayload } from '../interfaces/book-all-participants-to-course-execution.action.payload.interface';
import { BookAllParticipantsToCourseExecutionCommand } from '../models/book-all-participants-to-course-execution.command.model';
import { IModuleUserQuery } from 'app/library/module-user/interfaces/module-user.query.interface';
import { ModuleUserExtended } from 'app/library/module-user/models/module-user.extended.model';
import { IChangeProgramCompletionForParticipantsActionPayload } from '../interfaces/change-program-completion-for-participants.action.payload.interface';
import { IChangeProgramCompletionResult } from '../interfaces/change-program-completion-result.interface';
import { ChangeProgramCompletionForParticipantsCommand } from '../models/change-program-completion-for-participants.command.model';
import { IAssessmentCourseQuery } from '../interfaces/assessment-course.query.interface';
import { IProgramStructureWithProgramparticipantQuery } from '../interfaces/program-structure-with-program-participant.query.interface';
import { ProgramStructureWithProgramParticipant } from '../models/program-structure-with-program-participant.model';
import { ProgramExecutionMetrics } from '../models/program-execution-metrics.model';
import { IProgramExecutionMetricsQuery } from '../interfaces/program-execution-metrics.query.interface';
import { ITenantDashboardQuery } from 'app/library/tenant-dashboard/interfaces/tenant-dashboard.query.interface';
import { TenantDashboardChartId } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-id.enum';
import { TranslateService } from '@ngx-translate/core';
import { TenantDashboardChartType } from 'app/library/tenant-dashboard/enums/tenant-dashboard-chart-type.enum';
import { DateRange } from '../../../projects/shared/src/lib/models/date-range.model';
import { IDateRangeQuery } from '../../../projects/shared/src/lib/interfaces/date-range-query.interface';
import { RelevantExecutionsDateRangeCommand } from '../models/relevant-executions-date-range.command.model';
import { RelevantExecutionRuleEnum } from 'app/library/relevant-execution/enums/relevant-execution-rule.enum';
import { IProgramStructureWithGroupQuery } from '../interfaces/program-structure-with-default-execution.query.interface';
import { ProgramStructureWithGroup } from '../models/program-structure-with-group.model';
import { CourseRelevantExecution } from 'app/library/relevant-execution/models/course-relevant-execution.model';
import { ICourseRelevantExecutionQuery } from 'app/library/relevant-execution/interfaces/course-relevant-execution.query.interface';
import { IParticipantExecutionChangeQuery } from '../interfaces/participant-execution-change.query.interface';
import { ParticipantExecutionChange } from '../models/participant-execution-change.model';
import { PartcipantCourseGroup } from '../models/participant-course-group.model';
import { IParticipantCourseGroupQuery } from '../interfaces/participant-course-group.query.interface';
import { IProgramItemRemovalImpactQuery } from '../interfaces/program-item-removal-impact.query.interface';

@Injectable()
export class ProgramItemApiService extends EntityService<IProgramItemsWithGroupQuery, ProgramItemCommand> {
    pieChartOptions = {
        ticks: {
            precision: 0,
        },
        colors: ['#EAC196', '#B7AEDE', '#FBE78D', '#A3CAEA'],
        plugins: {
            legend: {
                display: false,
            },
        },
    };

    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment, private _translateService: TranslateService) {
        super(http, environment.apiUrl, 'course-structure/program-items');
    }

    getAll(categoryId: string): Observable<ProgramItemsWithGroup[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new ProgramItemsWithGroup(query))));
    }

    create(entity: ICreateProgramItemActionPayload): Observable<ProgramItem> {
        const data = new ProgramItemCommand(entity);
        return this.http.post<IProgramItemQuery>(`${this.environment.apiUrl}/${this.entities}`, data).pipe(map((query) => new ProgramItem(query)));
    }

    read(id: string): Observable<ProgramItem> {
        return this.http.get<IProgramItemQuery>(`${this.environment.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new ProgramItem(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }

    update(entity: ProgramItem): Observable<ProgramItem> {
        const data = new UpdateProgramItemCommand(entity);

        return this.http.patch<IProgramItemQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new ProgramItem(query)));
    }

    getProgramItemsWithGroup(id: string, tags: number[], search: string): Observable<IProgramItemsWithGroupQuery> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        return this.http.get<IProgramItemsWithGroupQuery>(`${this.environment.apiUrl}/course-structure/modules/${id}/program-items`, { params });
    }

    getCourseOfferingsByCategorySearch(categoryId: string, tags: number[], search: string): Observable<IProgramItemsWithGroupQuery> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        return this.http.get<IProgramItemsWithGroupQuery>(`${this.environment.apiUrl}/course-structure/modules/${categoryId}/program-items/search`, { params });
    }

    getAssessmentCourses(categoryId: string, tags: number[], search: string): Observable<IAssessmentCourseQuery> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        return this.http.get<IAssessmentCourseQuery>(`${this.environment.apiUrl}/course-structure/modules/${categoryId}/assessment-courses`, { params });
    }

    addCourseOfferingsToCourseCategory(entity: IAddCourseOfferingsToCourseCategoryActionPayload): Observable<ProgramItemsWithGroup> {
        const data = new AddCourseOfferingsToCourseGroupCommand(entity);
        return this.http.post<IProgramItemsWithGroupQuery>(`${this.environment.apiUrl}/${this.entities}`, data).pipe(map((query) => new ProgramItemsWithGroup(query)));
    }

    bookAllParticipantsToCourseExecution(entity: IBookAllParticipantsToCourseExecutionActionPayload): Observable<{ id: number }> {
        const data = new BookAllParticipantsToCourseExecutionCommand(entity);
        return this.http.post<{ id: number }>(`${this.environment.apiUrl}/course-structure/program-executions/${entity.id}/book-all/async`, data);
    }

    changeProgramCompletionForParticipants(entity: IChangeProgramCompletionForParticipantsActionPayload): Observable<IChangeProgramCompletionResult> {
        const data = new ChangeProgramCompletionForParticipantsCommand(entity);
        return this.http
            .post<IModuleUserQuery[] | { id: number }>(`${this.environment.apiUrl}/modules/${entity.id}/completion`, data, { observe: 'response' })
            .pipe(
                map((resp: HttpResponse<IModuleUserQuery[] | { id: number }>) => {
                    const asyncJobId = peekAsyncJobIdFromResponse(resp);
                    if (asyncJobId) {
                        return { moduleUsers: [], asyncJobId };
                    }

                    const queries = Array.isArray(resp.body) ? resp.body : [];
                    return { moduleUsers: queries.map((query) => new ModuleUserExtended(query)) };
                })
            );
    }

    getProgramExecutionStatistics(programExecutionId: string, offeringIds: string[]): Observable<ProgramItemsWithGroup[]> {
        let params = new HttpParams();

        if (offeringIds.length) {
            params = params.set('offeringIds', String(offeringIds));
        }

        return this.http
            .get<IProgramItemsWithGroupQuery[]>(`${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/statistics`, { params })
            .pipe(map((queries) => queries.map((query) => new ProgramItemsWithGroup(query))));
    }

    getRelevantExecutionsDateRange(id: string): Observable<DateRange> {
        return this.http
            .get<IDateRangeQuery>(`${this.environment.apiUrl}/course-structure/program-executions/${id}/relevant-executions-date-range`)
            .pipe(map((query) => new DateRange(query)));
    }

    updateRelevantExecutionsDateRange(id: string, dateRange: DateRange): Observable<DateRange> {
        const data = new RelevantExecutionsDateRangeCommand(dateRange);
        return this.http
            .post<IDateRangeQuery>(`${this.environment.apiUrl}/course-structure/program-executions/${id}/relevant-executions-date-range`, data)
            .pipe(map((query) => new DateRange(query)));
    }

    updateProgramItemRelevantExecutionRule(programItemId: string, relevantExecutionRule: RelevantExecutionRuleEnum): Observable<ProgramItem> {
        const data = { relevant_execution_rule: relevantExecutionRule };

        return this.http.patch<IProgramItemQuery>(`${this.apiUrl}/${this.entities}/${programItemId}`, data).pipe(map((query) => new ProgramItem(query)));
    }

    deleteRelevantExecutionsDateRange(programExecutionId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/course-structure/program-executions/${programExecutionId}/relevant-executions-date-range`);
    }

    getProgramExecutionMetrics(id: string): Observable<ProgramExecutionMetrics> {
        return this.http.get<IProgramExecutionMetricsQuery>(`${this.environment.apiUrl}/course-structure/program-executions/${id}/metrics`).pipe(
            map((query) => {
                const mappedCharts = this.getFullChartData(query);

                return new ProgramExecutionMetrics({ ...query, charts: mappedCharts });
            })
        );
    }

    getParticipantProgramExecution(programCourseId: string, userId: string): Observable<ProgramStructureWithProgramParticipant> {
        return this.http
            .get<IProgramStructureWithProgramparticipantQuery>(`${this.environment.apiUrl}/v1/course-structure/program-executions/${programCourseId}/participants/${userId}`)
            .pipe(map((query) => new ProgramStructureWithProgramParticipant(query)));
    }

    getTeamParticipantProgramExecution(teamId: string, programCourseId: string, userId: string): Observable<ProgramStructureWithProgramParticipant> {
        return this.http
            .get<IProgramStructureWithProgramparticipantQuery>(`${this.environment.apiUrl}/teams/${teamId}/program-executions/${programCourseId}/participants/${userId}`)
            .pipe(map((query) => new ProgramStructureWithProgramParticipant(query)));
    }

    getParticipantProgramExecutionGroup(programCourseId: string, courseGroupId: string, userId: string): Observable<PartcipantCourseGroup> {
        return this.http
            .get<IParticipantCourseGroupQuery>(
                `${this.environment.apiUrl}/course-structure/program-executions/${programCourseId}/course-groups/${courseGroupId}/participants/${userId}`
            )
            .pipe(map((query) => new PartcipantCourseGroup(query)));
    }

    getTeamParticipantProgramExecutionGroup(teamId: string, programCourseId: string, courseGroupId: string, userId: string): Observable<PartcipantCourseGroup> {
        return this.http
            .get<IParticipantCourseGroupQuery>(
                `${this.environment.apiUrl}/teams/${teamId}/program-executions/${programCourseId}/course-groups/${courseGroupId}/participants/${userId}`
            )
            .pipe(map((query) => new PartcipantCourseGroup(query)));
    }

    getProgramExecutionStructureWithDefaultExecutions(programExecutionId: string): Observable<ProgramStructureWithGroup[]> {
        return this.http
            .get<IProgramStructureWithGroupQuery[]>(`${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/structure-with-default-executions`)
            .pipe(map((queries) => queries.map((query) => new ProgramStructureWithGroup(query))));
    }

    getProgramExecutionCourseOfferingRelevantExecutions(
        programExecutionId: string,
        courseOfferingId: string,
        context?: string
    ): Observable<CourseRelevantExecution[]> {
        let params = new HttpParams();

        if (context) {
            params = params.set('context', context);
        }

        return this.http
            .get<ICourseRelevantExecutionQuery[]>(
                `${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/course-offerings/${courseOfferingId}/relevant-executions`,
                { params }
            )
            .pipe(map((queries) => queries.map((query) => new CourseRelevantExecution(query))));
    }

    getProgramItemRemovalImpact(programItemId: string): Observable<IProgramItemRemovalImpactQuery> {
        return this.http.get<IProgramItemRemovalImpactQuery>(`${this.environment.apiUrl}/course-structure/program-items/${programItemId}/removal-impact`);
    }

    getParticipantExecutionChanges(programExecutionId: string, userIds: string[], courseExecutionIds: string[]): Observable<ParticipantExecutionChange[]> {
        const data = {
            user_ids: userIds,
            course_execution_ids: courseExecutionIds,
        };

        return this.http
            .post<IParticipantExecutionChangeQuery[]>(`${this.environment.apiUrl}/course-structure/program-executions/${programExecutionId}/participants-execution-changes`, data)
            .pipe(map((queries) => queries.map((query) => new ParticipantExecutionChange(query))));
    }

    getFullChartData(query: IProgramExecutionMetricsQuery): ITenantDashboardQuery[] {
        return query.charts.map((chart) => {
            let updatedChart = null;

            switch (chart.chart_id) {
                case TenantDashboardChartId.CourseCompletionState:
                    chart.chart_data.labels = chart.chart_data.labels.map((label) =>
                        this._translateService.instant('PAGE.MODULE_FORM.TAB.CONTENT.PARTICIPANTS_PROGRESS.STATUSES.' + label.toUpperCase())
                    );

                    updatedChart = Object.assign({}, chart, {
                        type: TenantDashboardChartType.Pie,
                        options: {
                            ...this.pieChartOptions,
                        },
                    });

                    break;

                default:
                    break;
            }

            if (!updatedChart.aggregates) {
                updatedChart.chart_data = {
                    ...updatedChart.chart_data,
                    data_series: updatedChart.chart_data.data_series.map((dataset) => {
                        return {
                            ...dataset,
                            color: updatedChart.options.colors,
                            hoverBackgroundColor: updatedChart.options.colors,
                            hoverBorderColor: updatedChart.options.colors,
                        };
                    }),
                };
            } else {
                updatedChart.metric_color = updatedChart.options.colors[0];
            }

            return updatedChart;
        });
    }
}
